import React, { useState, useEffect, useCallback } from "react";
import MfaSetupPanel, { BackupCodesView } from "./MfaSetupPanel";

const api = (path) => `${process.env.REACT_APP_DEFAULTPATH}${path}`;

async function call(method, path, body) {
  const res = await fetch(api(path), {
    method,
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${localStorage.getItem("token")}` },
    ...(body ? { body: JSON.stringify(body) } : {}),
  });
  const data = await res.json().catch(() => ({}));
  return { ok: res.ok, data };
}

/**
 * "Two-step verification" card for My Profile.
 * Hidden when the server has the feature switched off or the client's policy is "off" and the user has not set it up.
 */
const MfaProfileCard = () => {
  const [status, setStatus] = useState(null);
  const [mode, setMode] = useState("idle"); // idle | setup | disable | codes | showCodes
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [newCodes, setNewCodes] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  const load = useCallback(async () => {
    const { ok, data } = await call("GET", "mfa/status");
    if (ok && data.status) setStatus(data);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const reset = () => {
    setMode("idle");
    setPassword("");
    setCode("");
    setError("");
  };

  if (!status || !status.featureEnabled) return null;
  if (status.policy === "off" && !status.enabled) return null;

  const submitDisable = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    const { ok, data } = await call("POST", "mfa/disable", { password, code });
    setBusy(false);
    if (ok) {
      reset();
      setNotice("Two-step verification is now off.");
      load();
    } else {
      setError(data.error || "Could not turn it off.");
    }
  };

  const submitNewCodes = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    const { ok, data } = await call("POST", "mfa/backup-codes", { code });
    setBusy(false);
    if (ok) {
      setNewCodes(data.backupCodes);
      setCode("");
      setMode("showCodes");
    } else {
      setError(data.error || "Could not create new codes.");
    }
  };

  return (
    <section className="asm-profile-section-card">
      <div className="asm-section-header">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--blue-500)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
        </svg>
        <span className="asm-section-title">Two-step verification</span>
        {status.enabled ? (
          <span className="asm-pill-badge asm-pill-green">On</span>
        ) : status.required ? (
          <span className="asm-pill-badge asm-pill-amber">Required</span>
        ) : (
          <span className="asm-pill-badge">Off</span>
        )}
      </div>

      <div className="asm-section-body" style={{ display: "flex", flexDirection: "column", gap: 14 }}>
        {notice && mode === "idle" && <div className="asm-field-help">{notice}</div>}

        {mode === "idle" && !status.enabled && (
          <>
            <p className="mb-0">
              Adds a second step when you sign in: a 6-digit code from an authenticator app on your phone, so a stolen password alone is not enough.
            </p>
            {status.required && status.graceDaysLeft && (
              <div className="asm-field-help" style={{ color: "var(--sev-high)" }}>
                Your organization requires this. Please set it up within {status.graceDaysLeft} day{status.graceDaysLeft === 1 ? "" : "s"}, otherwise you will be asked at your next login.
              </div>
            )}
          </>
        )}

        {mode === "idle" && status.enabled && (
          <p className="mb-0">
            Turned on{status.enrolledAt ? ` on ${new Date(status.enrolledAt).toLocaleDateString()}` : ""}. You have {status.backupCodesLeft} backup code{status.backupCodesLeft === 1 ? "" : "s"} left.
            {status.backupCodesLeft <= 2 && " Consider creating new ones."}
            {status.required && " Your organization requires two-step verification, so it cannot be turned off."}
          </p>
        )}

        {mode === "setup" && (
          <MfaSetupPanel
            token={localStorage.getItem("token")}
            onComplete={() => {
              reset();
              setNotice("Two-step verification is on.");
              load();
            }}
          />
        )}

        {mode === "disable" && (
          <form onSubmit={submitDisable} style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            <div className="asm-form-group">
              <label htmlFor="mfa-pass">Password</label>
              <input id="mfa-pass" type="password" className="asm-input" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="current-password" />
            </div>
            <div className="asm-form-group">
              <label htmlFor="mfa-code">Code from your app</label>
              <input id="mfa-code" className="asm-input mono" inputMode="numeric" placeholder="123456" value={code} onChange={(e) => setCode(e.target.value)} />
            </div>
            {error && <span className="asm-field-error">{error}</span>}
            <div style={{ display: "flex", gap: 8 }}>
              <button type="button" className="asm-btn-secondary" onClick={reset}>Cancel</button>
              <button type="submit" className="asm-btn-primary" disabled={busy || !password || !code.trim()}>Turn off</button>
            </div>
          </form>
        )}

        {mode === "codes" && (
          <form onSubmit={submitNewCodes} style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            <div className="asm-form-group">
              <label htmlFor="mfa-code2">Code from your app</label>
              <input id="mfa-code2" className="asm-input mono" inputMode="numeric" placeholder="123456" value={code} onChange={(e) => setCode(e.target.value)} />
              <span className="asm-field-help">Your old backup codes will stop working.</span>
            </div>
            {error && <span className="asm-field-error">{error}</span>}
            <div style={{ display: "flex", gap: 8 }}>
              <button type="button" className="asm-btn-secondary" onClick={reset}>Cancel</button>
              <button type="submit" className="asm-btn-primary" disabled={busy || !code.trim()}>Create new codes</button>
            </div>
          </form>
        )}

        {mode === "showCodes" && newCodes && (
          <BackupCodesView
            codes={newCodes}
            onSaved={() => {
              setNewCodes(null);
              reset();
              load();
            }}
          />
        )}
      </div>

      {mode === "idle" && (
        <div className="asm-section-footer">
          {!status.enabled && (
            <button type="button" className="asm-btn-primary" onClick={() => { setNotice(""); setMode("setup"); }}>
              Set up
            </button>
          )}
          {status.enabled && (
            <>
              <button type="button" className="asm-btn-secondary" onClick={() => { setNotice(""); setMode("codes"); }}>
                New backup codes
              </button>
              {!status.required && (
                <button type="button" className="asm-btn-secondary" onClick={() => { setNotice(""); setMode("disable"); }}>
                  Turn off
                </button>
              )}
            </>
          )}
        </div>
      )}
      {mode === "setup" && (
        <div className="asm-section-footer">
          <button type="button" className="asm-btn-secondary" onClick={reset}>Cancel</button>
        </div>
      )}
    </section>
  );
};

export default MfaProfileCard;
